/*
 * BottomBarMenuItem
 * @module BottomBarMenu
 * @flow
 */

import React, { Fragment } from 'react';
import { palette } from 'styled-theme';
import styled, { css } from 'styled-components/macro';

import { isReactNativeAppWebView } from '~/helpers';

export type BottomBarMenuItemProps = {
  children: () => React$Node,
  href?: string,
  icon: React$Element<*>,
  isNewTab?: boolean,
  onPseudoLinkTouch?: () => void,
  onTouch?: () => void,
};

const baseColor = palette('grayscale', 0, true);
const linkStyle = css`
  display: flex;
  align-items: center;
  height: 50px;
`;

const BottomBarMenuItemButton = styled.div`
  ${linkStyle}
`;

const BottomBarMenuItemLink = styled.a`
  ${linkStyle}
`;

const BottomBarMenuItemPseudoLink = styled.div`
  ${linkStyle}
`;

const BottomBarMenuItemIcon = styled.span`
  margin-right: 18.5px;
  color: ${baseColor};

  img {
    max-height: 24px;
    max-width: 24px;
  }
`;

const BottomBarMenuItemTitle = styled.span`
  color: ${baseColor};
  font-size: 14px;
`;

/**
 * BottomBarMenuItem React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - children
 * @param {string} [props.href] - if param is not null, will be an <a> tag
 * @param {React.Node<*>} [props.icon] - icon
 * @param {boolean} [props.isNewTab] - new tab opening flag
 * @param {Function} [props.onPseudoLinkTouch] - pseudo link touch handler
 * @param {Function} [props.onTouch] - touch handler
 * @example <caption>Simple usage</caption>
 * <BottomBarMenuItem icon={<IconWallet />}>My Wealth</BottomBarMenuItem>
 * @returns {React.Node<*>} React-component
 */
export const BottomBarMenuItem = ({
  children,
  href,
  icon,
  isNewTab,
  onPseudoLinkTouch,
  onTouch,
}: BottomBarMenuItemProps) => {
  const BottomBarMenuItemContent = () => (
    <Fragment>
      <BottomBarMenuItemIcon>{icon}</BottomBarMenuItemIcon>
      <BottomBarMenuItemTitle>{children}</BottomBarMenuItemTitle>
    </Fragment>
  );

  return href ? (
    isReactNativeAppWebView ? (
      <BottomBarMenuItemPseudoLink
        onTouchStart={onPseudoLinkTouch}
        testid="bottom-bar-menu-item"
      >
        <BottomBarMenuItemContent />
      </BottomBarMenuItemPseudoLink>
    ) : (
      <BottomBarMenuItemLink
        rel={isNewTab && 'noopener noreferrer'}
        target={isNewTab && '_blank'}
        href={href}
        testid="bottom-bar-menu-item"
      >
        <BottomBarMenuItemContent />
      </BottomBarMenuItemLink>
    )
  ) : (
    <BottomBarMenuItemButton
      onTouchStart={onTouch}
      testid="bottom-bar-menu-item"
    >
      <BottomBarMenuItemContent />
    </BottomBarMenuItemButton>
  );
};

BottomBarMenuItem.defaultProps = {
  onTouch: () => {},
};
